import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { LOGO_URL } from "@/constants/site";
import { useSectionContent } from "@/hooks/useContent";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

function StylizedShield() {
  return (
    <svg
      viewBox="0 0 500 410"
      fill="none"
      aria-hidden="true"
      className="w-full h-full"
    >
      <path
        d="M250 10
           C 292 34 344 58 414 74
           L 414 102
           C 380 107 360 120 352 142
           L 352 268
           C 360 290 380 303 414 308
           C 398 340 328 384 250 400
           C 172 384 102 340 86 308
           C 120 303 140 290 148 268
           L 148 142
           C 140 120 120 107 86 102
           L 86 74
           C 156 58 208 34 250 10
           Z"
        stroke="url(#barShieldOrange)"
        strokeWidth="20"
        strokeLinejoin="round"
        opacity="0.95"
      />
      <rect
        x="8"
        y="142"
        width="484"
        height="126"
        stroke="url(#barShieldOrange)"
        strokeWidth="20"
        opacity="0.95"
      />
      <defs>
        <linearGradient id="barShieldOrange" x1="0" y1="0" x2="500" y2="410">
          <stop offset="0" stopColor="#FF8A2A" />
          <stop offset="0.5" stopColor="#E8730C" />
          <stop offset="1" stopColor="#B45309" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function Hero() {
  const c = useSectionContent("hero");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden hero-spotlight pt-24 pb-16"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src={LOGO_URL}
          alt=""
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[130vmin] h-[130vmin] object-cover opacity-[0.16] blur-2xl scale-110"
        />
        <motion.div
          className="absolute left-1/2 top-1/2 aspect-[500/410] w-[88vmin] max-w-[1000px] blur-[2px]"
          style={{ x: "-50%", y: "-50%" }}
          animate={{ opacity: [0.28, 0.45, 0.28], scale: [1, 1.035, 1] }}
          transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <StylizedShield />
        </motion.div>
        <motion.div
          className="absolute left-1/2 top-1/2 w-[100vmin] h-[100vmin] rounded-full bg-[#E8730C]/15 blur-3xl"
          style={{ x: "-50%", y: "-50%" }}
          animate={{ opacity: [0.1, 0.28, 0.1], scale: [0.95, 1.08, 0.95] }}
          transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[#0A0A0B]/35" />
        <div className="absolute inset-x-0 top-1/3 h-px bg-gradient-to-r from-transparent via-[#C5A059]/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-24 h-px bg-gradient-to-r from-transparent via-[#C5A059]/10 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex flex-col items-center px-5">
        <motion.div
          style={{ y: logoY }}
          initial={{ scale: 0.9, opacity: 0, y: 40 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="absolute -inset-8 bg-[#C5A059]/20 blur-3xl rounded-full" aria-hidden="true" />
          <motion.img
            src="/logo-cutout.png"
            alt="Stemma Neapolis Free Chapter"
            data-testid="hero-logo"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
            className="relative w-72 sm:w-96 lg:w-[30rem] aspect-square [filter:drop-shadow(0_0_45px_rgba(197,160,89,0.35))_drop-shadow(0_18px_30px_rgba(0,0,0,0.6))]"
          />
        </motion.div>

        <div className="mt-12 text-center overflow-hidden">
          <div className="overflow-hidden">
            <motion.p
              custom={0}
              variants={lineVariants}
              initial="hidden"
              animate="visible"
              className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.5em] text-[#C5A059]"
            >
              {c.overline}
            </motion.p>
          </div>
          <h1 className="mt-4 font-display font-black uppercase leading-[0.95]">
            <span className="block overflow-hidden">
              <motion.span
                custom={1}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block text-4xl sm:text-6xl lg:text-8xl tracking-tight text-[#F4EBE1]"
                data-testid="hero-title-line-1"
              >
                {c.title1}
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                custom={2}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block text-4xl sm:text-6xl lg:text-8xl tracking-tight text-gold-gradient"
                data-testid="hero-title-line-2"
              >
                {c.title2}
              </motion.span>
            </span>
          </h1>
          <div className="overflow-hidden mt-5">
            <motion.p
              custom={3}
              variants={lineVariants}
              initial="hidden"
              animate="visible"
              className="text-sm sm:text-lg text-[#C0B9B0] tracking-[0.15em]"
              data-testid="hero-subtitle"
            >
              {c.subtitle}
            </motion.p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#merch"
            data-testid="hero-cta-shop"
            className="px-8 py-4 bg-[#C5A059] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E2C787] transition-all duration-300 hover:shadow-[0_0_40px_rgba(197,160,89,0.4)]"
          >
            {c.cta1}
          </a>
          <a
            href="#eventi"
            data-testid="hero-cta-events"
            className="px-8 py-4 border border-[#F4EBE1]/30 text-[#F4EBE1] font-mono text-xs uppercase tracking-[0.2em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
          >
            {c.cta2}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 flex flex-col items-center gap-2 text-[#78746D]"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.4em]">Scorri</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ChevronDown className="w-4 h-4" />
        </motion.div>
      </motion.div>
    </section>
  );
}
